import { createSlice } from "@reduxjs/toolkit";
import lightTheme from "@/themes/styles/light";
import darkTheme from "@/themes/styles/dark";

const themes = {
  light: lightTheme,
  dark: darkTheme,
};

const THEME_KEY = "app-theme";

/**
 * The theme this browser last chose, falling back to light.
 *
 * One reader for one key: the store's initial state and the boot-time paint in
 * `main.jsx` both come through here, so the two cannot start out disagreeing.
 * An unrecognised stored value is treated as no choice at all.
 */
export const getSavedTheme = () => {
  const saved = localStorage.getItem(THEME_KEY);
  return themes[saved] ? saved : "light";
};

export const initialState = {
  theme: getSavedTheme(),
};

/**
 * Write a theme onto `<html>`: the `data-theme` attribute that the `dark:`
 * variant and `custom.css` key off, the matching class, and every `--color-*`
 * variable the Tailwind tokens read.
 *
 * Nothing in the stylesheets gives those variables a fallback, so a document
 * that has not been through here paints with every token unresolved. That is
 * why `main.jsx` calls it before the first render and not only on a toggle.
 */
export const applyThemeToDOM = (themeName) => {
  const theme = themes[themeName];
  const root = document.documentElement;

  root.setAttribute("data-theme", themeName);

  if (themeName === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }

  if (theme) {
    Object.entries(theme.colors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  } else {
    console.warn(`Theme "${themeName}" not found.`);
  }
};

/** Paint the saved theme. Called once, before React mounts. */
export const applySavedTheme = () => applyThemeToDOM(getSavedTheme());

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme: (state, action) => {
      state.theme = action.payload;
      localStorage.setItem(THEME_KEY, action.payload);
      applyThemeToDOM(action.payload);
    },
  },
});

export const { setTheme } = themeSlice.actions;
export default themeSlice.reducer;